import React, { useState, Fragment, ComponentProps } from 'react';
import PropTypes from 'prop-types';
import './Page.scss';
import bemClass from '@upsales/components/Utils/bemClass';
import { Flex, Loader, Title } from '@upsales/components';

export type PageSidebarProvided = {
	expanded: boolean;
	setExpanded: (expanded: boolean) => void;
};

type BaseProps = {
	className?: string;
	transparent?: boolean;
	children?: any;
	header?: null | JSX.Element | (() => JSX.Element | null);
	sidebar?: null | JSX.Element | ((sidebarProvided: PageSidebarProvided) => JSX.Element | null);
	sidebarInitiallyExpanded?: boolean;
	sidebarExpanded?: boolean;
	setSidebarExpanded?: (expanded: boolean) => void;
	fullHeight?: boolean;
	contentLoading?: boolean;
	error?: string;
	static?: boolean;
	overflow?: boolean;
	asFlex?: boolean;
};

type PageProps =
	| (BaseProps & {
			asFlex?: false;
	  })
	| (BaseProps & {
			asFlex: true;
	  } & ComponentProps<typeof Flex>);

const Page = ({
	className,
	transparent = false,
	children,
	header = null,
	sidebar = null,
	sidebarInitiallyExpanded = false,
	sidebarExpanded: sidebarExpandedProp,
	setSidebarExpanded: setSidebarExpandedProp,
	fullHeight = false,
	contentLoading = false,
	static: isStatic = false,
	error = '',
	overflow = false,
	asFlex = false,
	...props
}: PageProps) => {
	const [internalSidebarExpanded, setInternalSidebarExpanded] = useState(sidebarInitiallyExpanded);
	const isControlled = sidebarExpandedProp !== undefined;
	const sidebarExpanded = isControlled ? sidebarExpandedProp : internalSidebarExpanded;

	const setSidebarExpanded = (expanded: boolean) => {
		if (isControlled) {
			setSidebarExpandedProp?.(expanded);
		} else {
			setInternalSidebarExpanded(expanded);
		}
	};

	const sidebarElement: JSX.Element | null =
		typeof sidebar === 'function'
			? sidebar({ expanded: sidebarExpanded, setExpanded: setSidebarExpanded })
			: sidebar;
	const headerElement: JSX.Element | null = typeof header === 'function' ? header() : header;
	const classes = new bemClass('Page', className).mod({
		transparent,
		'has-sidebar': !!sidebarElement && !error,
		'has-header': !!headerElement && !error,
		'full-height': fullHeight,
		static: isStatic,
		'no-frame': true
	});
	const content = contentLoading ? <Loader className={classes.elem('content-loader').b()} /> : children;
	const Component = asFlex ? Flex : 'div';
	return (
		<Component {...props} className={classes.b()}>
			{headerElement && !error ? <div className={classes.elem('header').b()}>{headerElement}</div> : null}
			{sidebarElement && !error ? (
				<Fragment>
					<div className={classes.elem('sidebar').mod({ expanded: sidebarExpanded }).b()}>
						{sidebarElement}
					</div>
					<div className={classes.elem('content').mod({ overflow }).b()}>{content}</div>
				</Fragment>
			) : error ? (
				<Title color="red" className={classes.elem('error').b()} center>
					{error}
				</Title>
			) : (
				content
			)}
		</Component>
	);
};

Page.propTypes = {
	className: PropTypes.string,
	transparent: PropTypes.bool,
	children: PropTypes.any,
	header: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
	sidebar: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
	sidebarInitiallyExpanded: PropTypes.bool,
	sidebarExpanded: PropTypes.bool,
	setSidebarExpanded: PropTypes.func,
	fullHeight: PropTypes.bool,
	contentLoading: PropTypes.bool,
	pageError: PropTypes.bool
};

export default Page;
